import Guid from "_shared/guids/guid";
import {
  ConversationRecord,
  forumDialogPath,
  proposalConversationPath,
  ProposalConversationStage,
  workAgreementConversationPath,
} from "messaging/conversations/conversation-links";
import { ProposalAction } from "work/proposals/__constants/proposal-lifecycle";
import Proposal from "work/proposals/proposal";
import WorkAgreement from "work/work-agreements/work-agreement";

/**
 * How the destination is read. Each answers the record as the reader may see it, and throws when the
 * reader is refused it or it does not exist: either way, its page is not one they can open.
 */
export type ConversationDestinationReaders = {
  readWorkAgreement: (workAgreementId: string) => Promise<WorkAgreement | null | undefined>;
  readProposal: (proposalId: string) => Promise<Proposal | null | undefined>;
};

export type ConversationDestinationRequest = {
  forumId: string;
  record: ConversationRecord;
  recordId: string;
  readerId: Guid | undefined;
};

/**
 * A revision chain longer than this is a loop in the data, not a real history. The walk stops on the
 * agreement it has reached rather than spinning.
 */
const longestRevisionChain = 25;

/**
 * Where a conversation opens for this reader (#5826): the record's page with its pane open on the
 * forum, or the generic chat dialog when the reader cannot open the record.
 *
 * - A work agreement opens on the latest revision the reader can see. Revisions share their
 *   conversation, which carries on in the latest one, so the walk follows each hired revision until
 *   the reader is refused the next one.
 * - A proposal opens on the page its state picks — a draft, or a submitted proposal — in edit for a
 *   draft's editor, in review for one of its reviewers and in view for anybody else. The server's
 *   available actions say who may edit, as they do for the proposals list (#7073).
 */
export async function resolveConversationDestination(
  request: ConversationDestinationRequest,
  readers: ConversationDestinationReaders,
): Promise<string> {
  const { forumId, record, recordId } = request;
  const fallback = forumDialogPath(forumId);
  if (!Guid.isValid(recordId)) return fallback;

  if (record === ConversationRecord.WorkAgreement) {
    const first = await readOrUndefined(() => readers.readWorkAgreement(recordId));
    if (!first?.id) return fallback;
    const latest = await latestVisibleRevision(first, readers);
    return workAgreementConversationPath({
      workAgreementId: latest.id!.value,
      forum: forumId,
      archived: Boolean(latest.archiveDate),
    });
  }

  const proposal = await readOrUndefined(() => readers.readProposal(recordId));
  if (!proposal?.id) return fallback;
  const stage = proposalStage(proposal);
  return proposalConversationPath({
    proposalId: proposal.id.value,
    stage,
    mode: proposalMode(proposal, stage, request.readerId),
    forum: forumId,
  });
}

function proposalMode(
  proposal: Proposal,
  stage: ProposalConversationStage,
  readerId: Guid | undefined,
): "edit" | "review" | "view" {
  const mayEdit = proposal.availableActions[ProposalAction.Edit].some((userId) => userId.isEqualTo(readerId));
  if (stage === "drafts" && mayEdit) return "edit";
  return proposal.getReviewer(readerId) ? "review" : "view";
}

async function latestVisibleRevision(
  agreement: WorkAgreement,
  readers: ConversationDestinationReaders,
): Promise<WorkAgreement> {
  let latest = agreement;
  for (let step = 0; step < longestRevisionChain; step++) {
    const nextId = latest.replacedByWorkAgreementId?.value;
    if (!nextId) break;
    const next = await readOrUndefined(() => readers.readWorkAgreement(nextId));
    if (!next?.id) break;
    latest = next;
  }
  return latest;
}

function proposalStage(proposal: Proposal): ProposalConversationStage {
  if (proposal.isArchived) return "archived";
  return proposal.isSubmitted ? "active" : "drafts";
}

/** The record, or `undefined` when the reader may not have it or it is not there. */
async function readOrUndefined<T>(read: () => Promise<T | null | undefined>): Promise<T | undefined> {
  try {
    return (await read()) ?? undefined;
  } catch {
    return undefined;
  }
}
